import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyDto,
  GetManyV2Dto,
  GetOneDto,
  PageableListDto,
  PageableListV2Dto,
  ResponseDto,
  UserDto,
} from '@fleet-beat/core';
import { Observable, catchError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/users`;
  private readonly baseAdminUrl: string = `${this.environment.apiEndpoint}/admin`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}
  getMany(params: GetManyDto, auth: AuthHeaders): Observable<PageableListDto<UserDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PageableListDto<UserDto>>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyV2(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<UserDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<UserDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getManyByIds(ids: string[], include: string, auth: AuthHeaders): Observable<UserDto[]> {
    return this.baseApiService.post(this.baseUrl, ids, { auth, params: { include } }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<UserDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  createOne(data: UserDto, auth: AuthHeaders): Observable<UserDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<UserDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponse(response)),
    );
  }
  patchOne(id: string, data: UserDto, auth: AuthHeaders): Observable<UserDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  updateAsAdminById(id: string, data: UserDto, auth: AuthHeaders): Observable<ResponseDto<UserDto>> {
    return this.baseApiService.patch(`${this.baseAdminUrl}/users/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  moveUserToTenantAsAdmin(id: string, tenantId: string, auth: AuthHeaders): Observable<ResponseDto<UserDto>> {
    return this.baseApiService.post(`${this.baseAdminUrl}/users/${id}/to-tenant/${tenantId}`, {}, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  createOneAsAdmin(data: UserDto, auth: AuthHeaders): Observable<UserDto> {
    return this.baseApiService.post(`${this.baseAdminUrl}/users`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyAsAdmin(params: GetManyDto, auth: AuthHeaders): Observable<PageableListV2Dto<UserDto>> {
    return this.baseApiService.get(`${this.baseAdminUrl}/users`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<UserDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<UserDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }
}
